// MapComponent.tsx
import React, { useEffect, useRef, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L, { LatLngExpression, Map } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { defaultMapCenter } from '@/env/config'

interface MapRow {
  imei: string
  time: string // ISO 8601 string representation of DtServer (e.g., "2025-01-17T12:34:56Z")
  lat: number  // Latitude as a floating-point number
  lng: number  // Longitude as a floating-point number
  altitude: number // Altitude in meters
  angle: number // Angle in degrees
  speed: number // Speed in meters per second
}

interface MapResponse {
  time: string // ISO 8601 string representation of the latest DtServer
  rows: MapRow[] | null   // Array of MapRow objects
}

export const MachineMapComponent = ({ services, session, imei }: {
  services: Services,
  session: CurrentSession,
  imei: string
}) => {
  const [map, setMap] = useState<MapResponse | undefined>(undefined)
  const [error, setError] = useState<boolean>(false)
  const mapRef = useRef<Map>(null)
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.invalidateSize();
    }
  }, [mapRef]); // Trigger resize on open

  async function fetchMap(imei: string): Promise<MapResponse> {
    return services.session.fetchWithToken(`/api/communal/map/byimei/${imei}`).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<MapResponse>()
      }
      const newResponse: MapResponse = await r.json()
      newResponse.rows = newResponse.rows || null
      return newResponse
    })
  }

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  useEffect(() => {
    try {
      fetchMap(imei).then(setMap, () => setError(true))
    } catch (e) {
      setError(true)
    }
  }, []);

  useEffect(() => {
    if (map === undefined) {
      return
    }
    const clock = async () => {
      if (map === undefined) {
        return
      }
      try {
        await fetchMap(imei).then(setMap)
      } catch (e) {
        console.error(e)
      }
      handle = setTimeout(clock, 2000)
    }
    let handle: NodeJS.Timeout = setTimeout(clock, 2000)
    return () => {
      clearTimeout(handle)
    }
  }, [map])

  if (error || map?.rows?.length === 0) {
    return <div className='main-search-error'>{session.translates.for('errors.no_tracking_device')}</div>
  }

  if (map === undefined) {
    return <div className='spinner'></div>
  }

  const firstRow = map?.rows?.length ? map.rows[0] : undefined;

  const center: LatLngExpression = (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || defaultMapCenter

  return (
    <MapContainer attributionControl={false} ref={mapRef} center={center} zoom={13} style={{ height: '80vh', width: '100%' }}>
      <TileLayer
        attribution='&copy <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {map.rows?.map((row) => (
        <Marker
          key={row.imei}
          position={row}
          icon={L.divIcon({
            className: 'car-icon',
            html: row.angle ? `<img src="/public/img/gps/vehicle_arrow.png" style="transform: rotate(${row.angle + 45}deg); width: 16px; margin: -9px" />` : `<img src="/public/img/gps/map_point.png" style="transform: rotate(${row.angle + 45}deg); width: 16px; margin: 8px" />`,
            iconSize: [32, 32], // Adjust size as needed
            iconAnchor: [16, 16], // Anchor point
          })} >
          <Popup>
            <div>
              <p>{session.translates.for('map.speed')}: {row.speed} km/h</p>
              <p>{session.translates.for('map.direction')}: {row.angle}°</p>
            </div>
          </Popup>
        </Marker>
      ))}

      {myposition ? <Marker
        key='my-location'
        position={myposition}
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 16px; margin: -16px auto auto 0px" />`,
          iconSize: [32, 32], // Adjust size as needed
          iconAnchor: [16, 16], // Anchor point
        })}></Marker> : null}
    </MapContainer>
  )
}

export default MachineMapComponent
